import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import messages from "../../assets/locale/messages";
import { setPageMainTitle } from "../../utils/Helpers";
import { Typography } from "@mui/material";
import Switch from "../../components/Switch";
import FilterLabel from "../../components/Filter/FilterLabel";
import FilterInput from "../../components/Filter/FilterInput";
import EmptyState from "../../components/EmptyState";
import Pagination from "../../components/Pagination";
import LookupsRequestsCard from "./LookupsRequestsCard";
import Button from "../../components/Button";
import RejectRequestModal from "./RejectRequestModal";
import {
  editServiceBulkRequest,
  getServicesRequest,
} from "../../store/Services/actions";
import { getProvidersRequest } from "../../store/Providers/actions";

const LookupsRequests = () => {
  const lang = useSelector((state) => state.locale.lang);
  const isLoading = useSelector((state) => state.loader);
  const list = useSelector((state) => state.services.list);
  const providersList = useSelector((state) => state.providers.list);
  const meta = useSelector((state) => state.services.meta);
  const { lookupsRequests, pagesTitles, shared } = messages[lang];
  const [page, setPage] = useState(1);
  const [isBulkSelection, setIsBulkSelection] = useState(false);
  const [filterProviderValue, setFilterProviderValue] = useState(null);
  const [filterServiceTypeValue, setFilterServiceTypeValue] = useState(null);
  const [providersOptions, setProvidersOptions] = useState([]);
  const [serviceTypeOptions, setServiceTypeOptions] = useState([]);
  const [selectedCards, setSelectedCards] = useState([]);
  const [selectedSingleCard, setSelectedSingleCard] = useState(null);
  const [showRejectRequestModal, setShowRejectRequestModal] = useState(false);
  const dispatch = useDispatch();

  useEffect(() => {
    setPageMainTitle("lookupsManagement");
    getProvidersLookup();
  }, []);

  useEffect(() => {
    getServices(page, filterProviderValue?.id, filterServiceTypeValue?.value);
  }, [page, filterProviderValue, filterServiceTypeValue]);

  useEffect(() => {
    if (filterProviderValue)
      setFilterProviderValue(
        providersOptions.find((option) => option.id === filterProviderValue.id)
      );
  }, [lang, providersOptions]);

  useEffect(() => {
    setServiceTypeOptions([
      {
        id: "multi_session",
        label: lookupsRequests.list.multiSessions,
        value: "multi_session",
      },
      {
        id: "single_service",
        label: lookupsRequests.list.singleService,
        value: "single_service",
      },
    ]);
  }, [lang]);

  useEffect(() => {
    if (filterServiceTypeValue)
      setFilterServiceTypeValue(
        serviceTypeOptions.find(
          (option) => option.value === filterServiceTypeValue.value
        )
      );
  }, [lang, serviceTypeOptions]);

  const getServices = (pageNo, providerId, serviceType) => {
    const requestParams = {
      page: pageNo,
      items: 10,
      status: "pending",
      service_provider_id: providerId,
    };

    if (serviceType === "multi_session") {
      requestParams.service_type = "multi_session";
    } else if (serviceType === "single_service") {
      requestParams.service_type = "single_service";
    }

    dispatch(getServicesRequest(requestParams));
  };

  const getProvidersLookup = () => {
    dispatch(
      getProvidersRequest({
        page: -1,
      })
    );
  };
  // To update filters options with lang change
  useEffect(() => {
    setProvidersOptions(
      providersList.map((option) => ({
        id: option.id,
        label: lang === "en" ? option.name_en : option.name_ar,
      }))
    );
  }, [lang, providersList]);

  const handleAcceptBulkRequest = () => {
    dispatch(
      editServiceBulkRequest(
        {
          ids: selectedCards,
          status: "accepted",
        },
        () => {
          getServices(page, filterProviderValue?.id);
        }
      )
    );
    setSelectedCards([]);
    setIsBulkSelection(false);
    setSelectedSingleCard(null);
  };

  const renderHeaderSection = (showActions) => (
    <>
      <div className="mb-4">
        <div className="title-search d-flex flex-wrap justify-content-between mb-4 gap-2">
          <div className="d-flex flex-column flex-md-row">
            <div>
              <Typography
                variant="h4"
                sx={{ flexGrow: 1 }}
                className={`page-title flex-grow-0 ${
                  lang === "en" ? "me-3" : "ms-3"
                }`}
              >
                {pagesTitles.lookupsRequests}
              </Typography>
            </div>
          </div>
          {showActions && (
            <div className="d-flex flex-column flex-md-row gap-2">
              <div className="mt-2">
                <FilterLabel />
              </div>
              <FilterInput
                placeholder={lookupsRequests.list.providerFilterPlaceHolder}
                options={providersOptions}
                value={filterProviderValue}
                name="Service Provider Filter"
                onChange={(value) => {
                  setFilterProviderValue(value);
                }}
                filterWrapperClass="w-content"
              />
              <FilterInput
                placeholder={lookupsRequests.list.serviceTypeFilterPlaceHolder}
                options={serviceTypeOptions}
                value={filterServiceTypeValue}
                name="Service Type Filter"
                onChange={(value) => {
                  setFilterServiceTypeValue(value);
                }}
                filterWrapperClass="w-content"
              />
            </div>
          )}
        </div>
        {!isLoading && list?.length > 1 && (
          <div className="filter-container">
            <div className="w-lg-50 mt-1 flex-wrap d-flex gap-3 justify-content-between w-100">
              <div className="d-flex flex-wrap gap-3">
                <div className="d-flex flex-nowrap gap-2">
                  <div className="fsize-20 text-dark">
                    {lookupsRequests.list.bulkSelection}
                  </div>
                  <Switch
                    id="isBulkSelection"
                    checked={isBulkSelection}
                    onChange={(e) => {
                      setSelectedCards([]);
                      setIsBulkSelection(e.target.checked);
                    }}
                  />
                </div>
                <div className="">
                  {isBulkSelection && (
                    <div className="fsize-20 text-dark ">
                      {selectedCards.length}{" "}
                      {lookupsRequests.list.itemsSelected}
                    </div>
                  )}
                </div>
              </div>
              {isBulkSelection && (
                <div className="d-flex flex-wrap justify-content gap-2">
                  <Button
                    onClick={() => {
                      setShowRejectRequestModal(true);
                    }}
                    outlined
                    label={lookupsRequests.card.rejectLabel}
                    className="bg-gray-light"
                    btnStyle="secondary"
                    labelClass="fsize-15 fweight-400 text-secondary px-5"
                    disabled={!selectedCards.length}
                  />
                  <Button
                    onClick={handleAcceptBulkRequest}
                    label={lookupsRequests.card.acceptLabel}
                    btnStyle="secondary"
                    labelClass="fsize-15 fweight-400 text-white px-5"
                    disabled={!selectedCards.length}
                  />
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );

  const renderLookupsRequestsList = () => {
    if (!isLoading && list?.length === 0 && filterProviderValue === null) {
      return (
        <>
          {renderHeaderSection(false)}
          <div className="bg-white rounded-4 categoriess-page d-flex justify-content-center align-items-center flex-column">
            <EmptyState
              subTitle={lookupsRequests.list.noData}
              showActionButton={false}
              NoDataListWithNoActionBtn={true}
            />
          </div>
        </>
      );
    } else if (
      !isLoading &&
      list?.length === 0 &&
      filterProviderValue != null
    ) {
      return (
        <>
          {renderHeaderSection(true)}
          <div className="bg-white rounded-4 categoriess-page d-flex justify-content-center align-items-center flex-column  mt-3">
            <EmptyState subTitle={shared.noSearch} showActionButton={false} />
          </div>
        </>
      );
    } else {
      return (
        <>
          {renderHeaderSection(true)}
          {list?.map((request) => (
            <LookupsRequestsCard
              key={request.id}
              requestData={request}
              isBulkSelection={isBulkSelection}
              selectedCards={selectedCards}
              setSelectedCards={setSelectedCards}
              setSelectedSingleCard={setSelectedSingleCard}
              setShowRejectRequestModal={setShowRejectRequestModal}
              onsuccess={() => {
                getServices(
                  page,
                  filterProviderValue?.id,
                  filterServiceTypeValue?.value
                );
              }}
            />
          ))}
          <div className="d-flex justify-content-center py-3">
            {meta?.pages > 1 && (
              <Pagination
                count={meta?.pages}
                page={meta?.currentPage || page}
                handleChange={(event, page) => {
                  setPage(page);
                }}
                defaultPage={1}
              />
            )}
          </div>
        </>
      );
    }
  };

  return (
    <div className="lookups-requests">
      {renderLookupsRequestsList()}
      <RejectRequestModal
        open={showRejectRequestModal}
        setOpen={setShowRejectRequestModal}
        isBulkSelection={isBulkSelection}
        selectedSingleCard={selectedSingleCard}
        selectedCards={selectedCards}
        setSelectedCards={setSelectedCards}
        setSelectedSingleCard={setSelectedSingleCard}
        setIsBulkSelection={setIsBulkSelection}
        onsuccess={() => {
          getServices(page, filterProviderValue?.id);
        }}
      />
    </div>
  );
};

export default LookupsRequests;
